import React from "react";
import { ITextInputErrorWarning } from "../../../store/@types/misc";
// constants and icons
import {
  IconLoader,
  IconTrash,
  IconCloudUp,
  IconCircleCheck,
} from "@tabler/icons-react";

// types
import { twMerge } from "tailwind-merge";

type IUpload = {
  title: string;
  name: string;
  onChange: (file: File | File[]) => void;
  onDelete?: () => void; // UPDATED to accept single or multiple files
  required?: boolean;
  completed: boolean;
  status: ITextInputErrorWarning;
  loading?: boolean;
  label?: string;
  className?: string;
  imgPreview?: string;
  multiple?: boolean; // NEW prop to toggle multi-upload
};

const UploadInput: React.FC<IUpload> = ({
  title,
  name,
  onChange,
  onDelete,
  required,
  completed,
  status,
  loading,
  label,
  className,
  imgPreview,
  multiple,
}) => {
  const toSentenceCase = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  return (
    <div
      onClick={() => document?.getElementById(name)?.click()}
      className={twMerge(
        "relative grid place-content-center w-full h-[213px] border-2 border-dashed cursor-pointer rounded-xl hover:bg-customgreen transition-colors duration-300 overflow-hidden",
        className,
        status
          ? "border-red-500"
          : completed
            ? "border-green-500"
            : "border-gray-300",
      )}
    >
      {imgPreview ? (
        <>
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute top-2 right-2 bg-white rounded-full z-50"
          >
            <IconTrash
              onClick={onDelete}
              size={32}
              className="cursor-pointer text-red-500 z-50"
            />
          </div>
          <div className="relative w-full h-full">
            <img
              src={imgPreview}
              alt={title}
              className="w-full h-full object-cover"
            />

            {loading && (
              <div className="absolute top-0 left-0 w-full h-full bg-[#00000050] backdrop-blur-sm flex items-center justify-center">
                <IconLoader size={32} className="animate-spin" />
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col justify-center items-center p-4">
          <div className="w-full flex items-center justify-center text-center">
            {loading ? (
              <div className="inline-flex flex-col items-center">
                <IconLoader size={32} className="animate-spin mt-3" />
                <span>
                  Uploading <span className="animate-bounce">.</span>...
                </span>
              </div>
            ) : completed ? (
              <div className="inline-flex flex-col items-center justify-center text-primary-500 mt-2">
                <IconCircleCheck size={52} className="text-primary-500" />
                <span className="text-base font-medium normal-case tracking-wide">
                  {toSentenceCase(title)} successful
                </span>
              </div>
            ) : (
              <div className="inline-flex flex-col items-center">
                <IconCloudUp
                  color={status ? "red" : completed ? "green" : "grey"}
                  size={52}
                  className="block"
                />
                <label className="block text-xl font-semibold ">
                  {title}
                  {required && <span className="text-[red]"> *</span>}
                </label>
                <span className="text-sm font-normal">{label} (MAX. 10MB)</span>
              </div>
            )}
          </div>
        </div>
      )}

      <input
        type="file"
        name={name}
        id={name}
        multiple={multiple} // NEW
        className="outline-none"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const { files } = e.currentTarget;
          if (files?.length) {
            const selectedFiles = Array.from(files);
            onChange(multiple ? selectedFiles : selectedFiles[0]); // Pass array if multiple=true
            // Reset input so same file can be selected again
            e.target.value = "";
          }
        }}
        hidden
      />
    </div>
  );
};

export default UploadInput;
